import type { Chart, ChartEvent, Plugin, Scale } from "chart.js";

import {
  clamp,
  getViewportTransform,
  getRadialSelectionBounds,
  getSelectionMode,
  getSelectionRect,
  getViewportSelectionBounds,
  isRadialScale,
  mapPointFromViewport,
} from "./geometry";
import type { IAxisZoomBounds, ICascadeChartZoomPayload, IChartZoomPayload, IViewportZoomBounds } from "./types";

export type {
  IAxisZoomBounds,
  IChartZoomPayload,
  IChartZoomState,
  IItemRangeZoom,
  IViewportZoomBounds,
  ICascadeChartZoomPayload,
} from "./types";

type SupportedChartType = "line" | "radar" | "scatter";

export interface ISelectionZoomPluginOptions {
  enabled?: boolean;
  captureOutsideCanvas?: boolean;
  minDragPx?: number;
  xScaleId?: string;
  yScaleIds?: string[];
  radialScaleId?: string;
  viewportMode?: boolean;
  viewport?: IViewportZoomBounds;
  cascadeDatasets?: Array<Array<{ x: number; y: unknown }>>;
  xOffset?: number;
  yOffset?: number;
  onZoom?: (payload: IChartZoomPayload | ICascadeChartZoomPayload) => void;
  onReset?: () => void;
}

interface INativeSelectionHandlers {
  canvasMouseDown: (event: MouseEvent) => void;
  windowMouseMove: (event: MouseEvent) => void;
  windowMouseUp: (event: MouseEvent) => void;
  windowBlur: () => void;
}

interface ISelectionZoomInternalState {
  mouseDown: boolean;
  dragging: boolean;
  startX: number | null;
  startY: number | null;
  currentX: number | null;
  currentY: number | null;
  suppressClickLock: number;
  viewportTransformApplied: boolean;
  overlayElement: HTMLDivElement | null;
  overlayParent: HTMLElement | null;
  overlayParentPreviousPosition: string | null;
  overlayFrame: number | null;
  nativeHandlers: INativeSelectionHandlers | null;
  nativeCanvas: HTMLCanvasElement | null;
  nativeWindowHandlersAttached: boolean;
  options: ISelectionZoomPluginOptions;
  activeDatasetIndex: number;
  reset: (notify?: boolean) => void;
}

type IChartWithSelectionZoom = Chart<SupportedChartType> & {
  selectionZoom?: ISelectionZoomInternalState;
};

const OVERLAY_FILL = "rgba(25, 65, 169, 0.12)";
const OVERLAY_STROKE = "rgba(25, 65, 169, 0.75)";
const DEFAULT_MIN_DRAG_PX = 8;

const requiresTwoDimensionalSelection = (options: ISelectionZoomPluginOptions): boolean =>
  Boolean(options.radialScaleId || options.viewportMode);

const isViewportMode = (options: ISelectionZoomPluginOptions): boolean => Boolean(options.viewportMode);

const canUseNativeSelection = (options: ISelectionZoomPluginOptions): boolean =>
  options.enabled !== false && (isViewportMode(options) || options.captureOutsideCanvas === true);

const applyViewportTransform = (chart: Chart, options: ISelectionZoomPluginOptions): boolean => {
  if (!options.viewportMode) {
    return false;
  }

  const transform = getViewportTransform(
    {
      left: 0,
      right: chart.width,
      top: 0,
      bottom: chart.height,
    },
    options.viewport,
  );

  if (!transform) {
    return false;
  }

  chart.ctx.save();
  chart.ctx.transform(transform.scale, 0, 0, transform.scale, transform.translateX, transform.translateY);

  return true;
};

const restoreViewportTransform = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;

  if (!state?.viewportTransformApplied) {
    return;
  }

  chart.ctx.restore();
  state.viewportTransformApplied = false;
};

const getCanvasPixelRatio = (chart: Chart): number => {
  if (!chart.canvas) {
    return 1;
  }

  const ratio = chart.width > 0 ? chart.canvas.width / chart.width : 1;

  return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
};

const hideSelectionOverlay = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;

  if (state?.overlayElement) {
    state.overlayElement.style.display = "none";
  }
};

const removeSelectionOverlay = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;

  if (!state) {
    return;
  }

  state.overlayElement?.remove();

  if (state.overlayParent && state.overlayParentPreviousPosition !== null) {
    state.overlayParent.style.position = state.overlayParentPreviousPosition;
  }

  state.overlayElement = null;
  state.overlayParent = null;
  state.overlayParentPreviousPosition = null;
};

const ensureSelectionOverlay = (chart: IChartWithSelectionZoom): HTMLDivElement | null => {
  const state = chart.selectionZoom;
  const parent = chart.canvas?.parentElement;

  if (!state || !parent || typeof document === "undefined") {
    return null;
  }

  if (state.overlayElement && state.overlayParent === parent) {
    return state.overlayElement;
  }

  removeSelectionOverlay(chart);

  if (typeof window !== "undefined" && window.getComputedStyle(parent).position === "static") {
    state.overlayParentPreviousPosition = parent.style.position;
    parent.style.position = "relative";
  }

  const overlay = document.createElement("div");
  overlay.style.position = "absolute";
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.pointerEvents = "none";
  overlay.style.zIndex = "20";
  overlay.style.display = "none";
  overlay.style.boxSizing = "border-box";
  overlay.style.border = `1px dashed ${OVERLAY_STROKE}`;
  overlay.style.background = OVERLAY_FILL;
  overlay.style.transformOrigin = "0 0";
  overlay.style.willChange = "transform,width,height";

  parent.appendChild(overlay);
  state.overlayElement = overlay;
  state.overlayParent = parent;

  return overlay;
};

const cancelOverlayFrame = (state: ISelectionZoomInternalState): void => {
  if (state.overlayFrame === null || typeof window === "undefined") {
    state.overlayFrame = null;
    return;
  }

  window.cancelAnimationFrame(state.overlayFrame);
  state.overlayFrame = null;
};

const isLeftMouseButton = (event?: MouseEvent): boolean => {
  return !event || typeof event.button !== "number" || event.button === 0;
};

const isInsideChartArea = (chart: Chart, x: number, y: number): boolean => {
  const { left, right, top, bottom } = chart.chartArea;
  return x >= left && x <= right && y >= top && y <= bottom;
};

const toNumericBounds = (value1: unknown, value2: unknown): IAxisZoomBounds | null => {
  const min = Number(value1);
  const max = Number(value2);

  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return null;
  }

  return {
    min: Math.min(min, max),
    max: Math.max(min, max),
  };
};

const resolveScale = (chart: Chart, scaleId: string | undefined): Scale | null => {
  if (!scaleId) {
    return null;
  }

  return chart.scales?.[scaleId] ?? null;
};

const getRelativePointer = (chart: Chart, event: MouseEvent): { x: number; y: number } | null => {
  if (!chart.canvas) {
    return null;
  }

  const rect = chart.canvas.getBoundingClientRect();

  if (!rect.width || !rect.height || !chart.width || !chart.height) {
    return null;
  }

  return {
    x: (event.clientX - rect.left) * (chart.width / rect.width),
    y: (event.clientY - rect.top) * (chart.height / rect.height),
  };
};

const consumeSelectionEvent = (event: MouseEvent): void => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

const detachNativeWindowHandlers = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;

  if (!state?.nativeHandlers || !state.nativeWindowHandlersAttached || typeof window === "undefined") {
    return;
  }

  window.removeEventListener("mousemove", state.nativeHandlers.windowMouseMove, true);
  window.removeEventListener("mouseup", state.nativeHandlers.windowMouseUp, true);
  window.removeEventListener("blur", state.nativeHandlers.windowBlur, true);
  state.nativeWindowHandlersAttached = false;
};

const attachNativeWindowHandlers = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;

  if (!state?.nativeHandlers || state.nativeWindowHandlersAttached || typeof window === "undefined") {
    return;
  }

  window.addEventListener("mousemove", state.nativeHandlers.windowMouseMove, true);
  window.addEventListener("mouseup", state.nativeHandlers.windowMouseUp, true);
  window.addEventListener("blur", state.nativeHandlers.windowBlur, true);
  state.nativeWindowHandlersAttached = true;
};

const buildRadialSelectionPayload = (
  chart: Chart,
  scaleId: string,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): IChartZoomPayload | null => {
  const radialScale = resolveScale(chart, scaleId);

  if (!isRadialScale(radialScale)) {
    return null;
  }

  const radialBounds = getRadialSelectionBounds(radialScale, getSelectionRect(startX, startY, currentX, currentY));

  if (!radialBounds) {
    return null;
  }

  return {
    axisMode: "y",
    y: {
      [scaleId]: radialBounds,
    },
  };
};

const buildViewportSelectionPayload = (
  chart: Chart,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): IChartZoomPayload | null => {
  const viewportBounds = getViewportSelectionBounds(
    {
      left: 0,
      right: chart.width,
      top: 0,
      bottom: chart.height,
    },
    getSelectionRect(startX, startY, currentX, currentY),
  );

  if (!viewportBounds) {
    return null;
  }

  return {
    axisMode: "viewport",
    viewport: viewportBounds,
  };
};

const buildCartesianSelectionPayload = (
  chart: Chart,
  options: ISelectionZoomPluginOptions,
  axisMode: "x" | "y" | "xy",
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): IChartZoomPayload | null => {
  const opt = options ?? {};
  const nextPayload: IChartZoomPayload = { axisMode };

  if (axisMode === "x" || axisMode === "xy") {
    const xScale = resolveScale(chart, opt.xScaleId ?? "x");

    if (!xScale || typeof xScale.getValueForPixel !== "function") {
      return null;
    }

    const xBounds = toNumericBounds(
      xScale.getValueForPixel(Math.min(startX, currentX)),
      xScale.getValueForPixel(Math.max(startX, currentX)),
    );

    if (!xBounds) {
      return null;
    }

    nextPayload.x = xBounds;
  }

  if (axisMode === "y" || axisMode === "xy") {
    const yScaleIds = opt.yScaleIds ?? ["y"];
    const yBoundsMap: Record<string, IAxisZoomBounds> = {};

    yScaleIds.forEach(scaleId => {
      const scale = resolveScale(chart, scaleId);

      if (!scale || typeof scale.getValueForPixel !== "function") {
        return;
      }

      const bounds = toNumericBounds(scale.getValueForPixel(startY), scale.getValueForPixel(currentY));

      if (bounds) {
        yBoundsMap[scaleId] = bounds;
      }
    });

    if (Object.keys(yBoundsMap).length === 0) {
      return null;
    }

    nextPayload.y = yBoundsMap;
  }

  return nextPayload;
};

const buildCascadeSelectionPayload = (
  chart: Chart,
  options: ISelectionZoomPluginOptions,
  activeDatasetIndex: number,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): ICascadeChartZoomPayload | null => {
  const opt = options ?? {};

  const axisMode = getSelectionMode(startX, startY, currentX, currentY, opt.minDragPx ?? DEFAULT_MIN_DRAG_PX);

  if (!axisMode) {
    return null;
  }

  const payload: ICascadeChartZoomPayload = {
    axisMode,
    baseDatasetIndex: activeDatasetIndex,
  };

  const datasetCount = opt.cascadeDatasets?.length ?? 1;

  const xOffset = opt.xOffset ?? 0;
  const yOffset = opt.yOffset ?? 0;

  if (axisMode === "x" || axisMode === "xy") {
    const xScale = resolveScale(chart, opt.xScaleId ?? "x");

    if (!xScale || typeof xScale.getValueForPixel !== "function") {
      return null;
    }

    const dx = activeDatasetIndex * xOffset;

    const selectionX = toNumericBounds(
      (xScale.getValueForPixel(Math.min(startX, currentX)) ?? 0) - dx,
      (xScale.getValueForPixel(Math.max(startX, currentX)) ?? 0) - dx,
    );

    if (!selectionX) {
      return null;
    }

    payload.selection = {
      ...payload.selection,
      x: selectionX,
    };
    payload.selectionX = selectionX;

    payload.x = {
      min: selectionX.min,
      max: selectionX.max + (datasetCount - 1) * xOffset,
    };
  }

  if (axisMode === "y" || axisMode === "xy") {
    const yScaleIds = opt.yScaleIds ?? ["y"];

    const yBoundsMap: Record<string, IAxisZoomBounds> = {};

    let selectionY: IAxisZoomBounds | undefined;

    yScaleIds.forEach(scaleId => {
      const scale = resolveScale(chart, scaleId);

      if (!scale || typeof scale.getValueForPixel !== "function") {
        return;
      }

      const dy = activeDatasetIndex * yOffset;

      const bounds = toNumericBounds(
        (scale.getValueForPixel(startY) ?? 0) - dy,
        (scale.getValueForPixel(currentY) ?? 0) - dy,
      );

      if (!bounds) {
        return;
      }

      selectionY = bounds;

      yBoundsMap[scaleId] = {
        min: bounds.min,
        max: bounds.max + (datasetCount - 1) * yOffset,
      };
    });

    if (Object.keys(yBoundsMap).length === 0) {
      return null;
    }

    payload.y = yBoundsMap;

    if (selectionY) {
      payload.selection = {
        ...payload.selection,
        y: selectionY,
      };

      payload.selectionY = selectionY;
    }
  }

  const items = opt.cascadeDatasets?.[activeDatasetIndex];

  if (items && items.length > 0 && payload.selection?.x) {
    let startIndex = -1;
    let endIndex = -1;

    for (let i = 0; i < items.length; i++) {
      const x = items[i].x;

      if (x >= payload.selection.x.min && x <= payload.selection.x.max) {
        if (startIndex === -1) {
          startIndex = i;
        }

        endIndex = i;
      }
    }

    if (startIndex !== -1) {
      payload.itemRange = {
        datasetIndex: activeDatasetIndex,
        startIndex,
        endIndex,
      };
    }
  }

  payload.globalBounds = {
    x: payload.x,

    y: payload.y && opt.yScaleIds?.length ? payload.y[opt.yScaleIds[0]] : undefined,
  };

  return payload;
};

const clearSelectionState = (
  chart: IChartWithSelectionZoom,
  options?: ISelectionZoomPluginOptions,
  notify = true,
  redraw = true,
): void => {
  const state = chart.selectionZoom;

  if (!state) {
    return;
  }

  detachNativeWindowHandlers(chart);
  cancelOverlayFrame(state);
  hideSelectionOverlay(chart);

  state.mouseDown = false;
  state.dragging = false;
  state.startX = null;
  state.startY = null;
  state.currentX = null;
  state.currentY = null;
  state.suppressClickLock = 0;

  if (notify) {
    options?.onReset?.();
  }

  if (redraw && chart.canvas) {
    chart.draw();
  }
};

const buildSelectionPayload = (
  chart: Chart,
  options: ISelectionZoomPluginOptions,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): IChartZoomPayload | null => {
  const minDragPx = options.minDragPx ?? DEFAULT_MIN_DRAG_PX;
  const axisMode = getSelectionMode(startX, startY, currentX, currentY, minDragPx);

  if (!axisMode) {
    return null;
  }

  if (options.viewportMode) {
    if (axisMode !== "xy") {
      return null;
    }

    return buildViewportSelectionPayload(chart, startX, startY, currentX, currentY);
  }

  if (options.radialScaleId) {
    if (axisMode !== "xy") {
      return null;
    }

    return buildRadialSelectionPayload(chart, options.radialScaleId, startX, startY, currentX, currentY);
  }

  return buildCartesianSelectionPayload(chart, options, axisMode, startX, startY, currentX, currentY);
};

const drawSelectionOverlay = (
  chart: Chart,
  state: ISelectionZoomInternalState,
  options: ISelectionZoomPluginOptions,
): boolean => {
  if (state.startX === null || state.startY === null || state.currentX === null || state.currentY === null) {
    return false;
  }

  const minDragPx = options.minDragPx ?? DEFAULT_MIN_DRAG_PX;
  const selectionMode = getSelectionMode(state.startX, state.startY, state.currentX, state.currentY, minDragPx);

  if (!selectionMode || (requiresTwoDimensionalSelection(options) && selectionMode !== "xy")) {
    return false;
  }

  const ctx = chart.ctx;
  const left = Math.min(state.startX, state.currentX);
  const right = Math.max(state.startX, state.currentX);
  const top = Math.min(state.startY, state.currentY);
  const bottom = Math.max(state.startY, state.currentY);
  const pixelRatio = getCanvasPixelRatio(chart);

  ctx.save();
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.strokeStyle = OVERLAY_STROKE;
  ctx.fillStyle = OVERLAY_FILL;
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 3]);

  if (requiresTwoDimensionalSelection(options) || selectionMode === "xy") {
    ctx.beginPath();
    ctx.rect(left, top, right - left, bottom - top);
    ctx.fill();
    ctx.stroke();
  } else if (selectionMode === "x") {
    ctx.beginPath();
    ctx.moveTo(left, state.startY);
    ctx.lineTo(right, state.startY);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(state.startX, top);
    ctx.lineTo(state.startX, bottom);
    ctx.stroke();
  }

  ctx.restore();

  return true;
};

const updateSelectionOverlay = (
  chart: IChartWithSelectionZoom,
  state: ISelectionZoomInternalState,
  options: ISelectionZoomPluginOptions,
  showBelowDragThreshold = false,
): boolean => {
  if (state.startX === null || state.startY === null || state.currentX === null || state.currentY === null) {
    hideSelectionOverlay(chart);
    return false;
  }

  const minDragPx = options.minDragPx ?? DEFAULT_MIN_DRAG_PX;
  const selectionMode = getSelectionMode(state.startX, state.startY, state.currentX, state.currentY, minDragPx);

  if (
    !showBelowDragThreshold &&
    (!selectionMode || (requiresTwoDimensionalSelection(options) && selectionMode !== "xy"))
  ) {
    hideSelectionOverlay(chart);
    return false;
  }

  const overlay = ensureSelectionOverlay(chart);
  const parent = state.overlayParent;

  if (!overlay || !parent || !chart.canvas) {
    return false;
  }

  const canvasRect = chart.canvas.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();
  const left = Math.min(state.startX, state.currentX);
  const right = Math.max(state.startX, state.currentX);
  const top = Math.min(state.startY, state.currentY);
  const bottom = Math.max(state.startY, state.currentY);
  const canvasOffsetX = canvasRect.left - parentRect.left;
  const canvasOffsetY = canvasRect.top - parentRect.top;
  const width = Math.max(1, right - left);
  const height = Math.max(1, bottom - top);

  overlay.style.display = "block";
  overlay.style.transform = `translate3d(${canvasOffsetX + left}px, ${canvasOffsetY + top}px, 0)`;
  overlay.style.width = `${width}px`;
  overlay.style.height = `${height}px`;

  return true;
};

const scheduleSelectionOverlayUpdate = (chart: IChartWithSelectionZoom, state: ISelectionZoomInternalState): void => {
  if (state.overlayElement?.style.display !== "block") {
    updateSelectionOverlay(chart, state, state.options, true);
    return;
  }

  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    updateSelectionOverlay(chart, state, state.options, true);
    return;
  }

  if (state.overlayFrame !== null) {
    return;
  }

  state.overlayFrame = window.requestAnimationFrame(() => {
    state.overlayFrame = null;
    updateSelectionOverlay(chart, state, state.options, true);
  });
};

const updateNativeSelection = (chart: IChartWithSelectionZoom, event: MouseEvent): void => {
  const state = chart.selectionZoom;

  if (!state?.mouseDown) {
    return;
  }

  const pointer = getRelativePointer(chart, event);

  if (!pointer) {
    clearSelectionState(chart, state.options, false);
    return;
  }

  state.currentX = clamp(pointer.x, chart.chartArea.left, chart.chartArea.right);
  state.currentY = clamp(pointer.y, chart.chartArea.top, chart.chartArea.bottom);

  const selectionMode =
    state.startX !== null && state.startY !== null
      ? getSelectionMode(
          state.startX,
          state.startY,
          state.currentX,
          state.currentY,
          state.options.minDragPx ?? DEFAULT_MIN_DRAG_PX,
        )
      : null;

  state.dragging = Boolean(
    selectionMode && (!requiresTwoDimensionalSelection(state.options) || selectionMode === "xy"),
  );
  scheduleSelectionOverlayUpdate(chart, state);
};

const completeNativeSelection = (chart: IChartWithSelectionZoom, event: MouseEvent): void => {
  const state = chart.selectionZoom;

  if (!state?.mouseDown) {
    return;
  }

  updateNativeSelection(chart, event);
  cancelOverlayFrame(state);
  updateSelectionOverlay(chart, state, state.options, true);

  if (
    state.dragging &&
    state.startX !== null &&
    state.startY !== null &&
    state.currentX !== null &&
    state.currentY !== null
  ) {
    const payload = buildSelectionPayload(
      chart,
      state.options,
      state.startX,
      state.startY,
      state.currentX,
      state.currentY,
    );

    if (payload) {
      clearSelectionState(chart, state.options, false);
      state.suppressClickLock = 2;
      state.options.onZoom?.(payload);
      return;
    }
  }

  clearSelectionState(chart, state.options, false);
};

const startNativeSelection = (chart: IChartWithSelectionZoom, event: MouseEvent): boolean => {
  const state = chart.selectionZoom;

  if (!state || !canUseNativeSelection(state.options) || !isLeftMouseButton(event)) {
    return false;
  }

  const pointer = getRelativePointer(chart, event);

  if (!pointer || !isInsideChartArea(chart, pointer.x, pointer.y)) {
    return false;
  }

  consumeSelectionEvent(event);
  clearSelectionState(chart, state.options, false, false);
  state.mouseDown = true;
  state.dragging = false;
  state.startX = clamp(pointer.x, chart.chartArea.left, chart.chartArea.right);
  state.startY = clamp(pointer.y, chart.chartArea.top, chart.chartArea.bottom);
  state.currentX = state.startX;
  state.currentY = state.startY;
  ensureSelectionOverlay(chart);
  attachNativeWindowHandlers(chart);

  return true;
};

const installNativeSelection = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;
  const canvas = chart.canvas;

  if (!state || state.nativeHandlers || !canvas) {
    return;
  }

  // Native listeners preserve selection when the pointer leaves the canvas during a drag.
  const handlers: INativeSelectionHandlers = {
    canvasMouseDown: event => {
      startNativeSelection(chart, event);
    },
    windowMouseMove: event => {
      if (!canUseNativeSelection(state.options)) {
        clearSelectionState(chart, state.options, false);
        return;
      }

      if (!state.mouseDown) {
        return;
      }

      consumeSelectionEvent(event);

      if (typeof event.buttons === "number" && (event.buttons & 1) === 0) {
        clearSelectionState(chart, state.options, false);
        return;
      }

      updateNativeSelection(chart, event);
    },
    windowMouseUp: event => {
      if (!canUseNativeSelection(state.options)) {
        clearSelectionState(chart, state.options, false);
        return;
      }

      if (!state.mouseDown || !isLeftMouseButton(event)) {
        return;
      }

      consumeSelectionEvent(event);
      completeNativeSelection(chart, event);
    },
    windowBlur: () => {
      if (state.mouseDown || state.dragging) {
        clearSelectionState(chart, state.options, false);
      }
    },
  };

  state.nativeHandlers = handlers;
  state.nativeCanvas = canvas;
  canvas.addEventListener("mousedown", handlers.canvasMouseDown, true);
};

const removeNativeSelection = (chart: IChartWithSelectionZoom): void => {
  const state = chart.selectionZoom;

  if (!state?.nativeHandlers) {
    return;
  }

  detachNativeWindowHandlers(chart);
  state.nativeCanvas?.removeEventListener("mousedown", state.nativeHandlers.canvasMouseDown, true);
  state.nativeHandlers = null;
  state.nativeCanvas = null;
};

const createSelectionZoomState = (
  chart: IChartWithSelectionZoom,
  options: ISelectionZoomPluginOptions,
): ISelectionZoomInternalState => ({
  mouseDown: false,
  dragging: false,
  startX: null,
  startY: null,
  currentX: null,
  currentY: null,
  suppressClickLock: 0,
  viewportTransformApplied: false,
  overlayElement: null,
  overlayParent: null,
  overlayParentPreviousPosition: null,
  overlayFrame: null,
  nativeHandlers: null,
  nativeCanvas: null,
  nativeWindowHandlersAttached: false,
  activeDatasetIndex: 0,
  options,
  reset: (notify = true) => clearSelectionState(chart, chart.selectionZoom?.options, notify),
});

export const SelectionZoomPlugin: Plugin<SupportedChartType> = {
  id: "selectionZoom",

  afterInit(chart, _args, options?: ISelectionZoomPluginOptions) {
    const chartWithSelectionZoom = chart as IChartWithSelectionZoom;

    chartWithSelectionZoom.selectionZoom = createSelectionZoomState(chartWithSelectionZoom, options ?? {});
    installNativeSelection(chartWithSelectionZoom);
  },

  beforeEvent(chart, args, pluginOptions?: ISelectionZoomPluginOptions) {
    const options = pluginOptions ?? {};

    if (options.enabled === false || !isViewportMode(options)) {
      return;
    }

    const event = args.event as ChartEvent & { native?: MouseEvent };
    const eventType = event.native?.type ?? event.type;

    if (eventType === "mouseout" || eventType === "mouseleave") {
      return;
    }

    const pointerX = typeof event.x === "number" ? event.x : null;
    const pointerY = typeof event.y === "number" ? event.y : null;

    if (pointerX === null || pointerY === null) {
      return;
    }

    const transform = getViewportTransform(
      {
        left: 0,
        right: chart.width,
        top: 0,
        bottom: chart.height,
      },
      options.viewport,
    );

    if (!transform) {
      return;
    }

    const originalPointer = mapPointFromViewport({ x: pointerX, y: pointerY }, transform);

    event.x = clamp(originalPointer.x, 0, chart.width);
    event.y = clamp(originalPointer.y, 0, chart.height);
  },

  beforeDraw(chart, _args, pluginOptions?: ISelectionZoomPluginOptions) {
    const options = pluginOptions ?? {};
    const chartWithSelectionZoom = chart as IChartWithSelectionZoom;
    const state = chartWithSelectionZoom.selectionZoom;

    restoreViewportTransform(chartWithSelectionZoom);

    if (!state) {
      return;
    }

    state.options = options;

    if (options.enabled === false) {
      return;
    }

    state.viewportTransformApplied = applyViewportTransform(chart, options);
  },

  afterEvent(chart, args, pluginOptions?: ISelectionZoomPluginOptions) {
    const options = pluginOptions ?? {};
    const isCascadeMode = !!options.cascadeDatasets?.length;
    const chartWithSelectionZoom = chart as IChartWithSelectionZoom;
    const state = chartWithSelectionZoom.selectionZoom;

    if (!state) {
      return;
    }

    state.options = options;

    if (options.enabled === false) {
      return;
    }

    if (canUseNativeSelection(options)) {
      return;
    }

    const event = args.event as ChartEvent & { native?: MouseEvent };
    const nativeEvent = event.native;

    if (!nativeEvent) {
      return;
    }

    const minDragPx = options.minDragPx ?? DEFAULT_MIN_DRAG_PX;
    const type = nativeEvent.type;
    const pointerX = typeof event.x === "number" ? event.x : null;
    const pointerY = typeof event.y === "number" ? event.y : null;

    if (type === "mouseout" || type === "mouseleave") {
      if (state.mouseDown || state.dragging) {
        clearSelectionState(chartWithSelectionZoom, options, false);
      }

      return;
    }

    if (state.mouseDown && type === "mouseup" && !isLeftMouseButton(nativeEvent)) {
      clearSelectionState(chartWithSelectionZoom, options, false);
      return;
    }

    if (
      state.mouseDown &&
      type === "mousemove" &&
      typeof nativeEvent.buttons === "number" &&
      (nativeEvent.buttons & 1) === 0
    ) {
      clearSelectionState(chartWithSelectionZoom, options, false);
      return;
    }

    if (pointerX === null || pointerY === null) {
      if (state.mouseDown || state.dragging) {
        clearSelectionState(chartWithSelectionZoom, options, false);
      }

      return;
    }

    if (type === "mousedown" && isLeftMouseButton(nativeEvent) && isInsideChartArea(chart, pointerX, pointerY)) {
      if (isCascadeMode) {
        const elements = chart.getElementsAtEventForMode(nativeEvent, "nearest", { intersect: false }, false);
        state.activeDatasetIndex = elements.length > 0 ? elements[0].datasetIndex : 0;
      }

      const nativePointer = getRelativePointer(chart, nativeEvent);
      const rawX = nativePointer ? nativePointer.x : pointerX;
      const rawY = nativePointer ? nativePointer.y : pointerY;

      state.mouseDown = true;
      state.dragging = false;
      state.startX = clamp(rawX, chart.chartArea.left, chart.chartArea.right);
      state.startY = clamp(rawY, chart.chartArea.top, chart.chartArea.bottom);
      state.currentX = state.startX;
      state.currentY = state.startY;
      chart.draw();
      return;
    }

    if (!state.mouseDown) {
      return;
    }

    if (type === "mousemove") {
      const nativePointer = getRelativePointer(chart, nativeEvent);
      const rawX = nativePointer ? nativePointer.x : pointerX;
      const rawY = nativePointer ? nativePointer.y : pointerY;
      state.currentX = clamp(rawX, chart.chartArea.left, chart.chartArea.right);
      state.currentY = clamp(rawY, chart.chartArea.top, chart.chartArea.bottom);
      const selectionMode =
        state.startX !== null && state.startY !== null
          ? getSelectionMode(state.startX, state.startY, state.currentX, state.currentY, minDragPx)
          : null;

      state.dragging = Boolean(selectionMode && (!requiresTwoDimensionalSelection(options) || selectionMode === "xy"));
      chart.draw();
      return;
    }

    if (type !== "mouseup" || !isLeftMouseButton(nativeEvent)) {
      return;
    }

    state.currentX = clamp(pointerX, chart.chartArea.left, chart.chartArea.right);
    state.currentY = clamp(pointerY, chart.chartArea.top, chart.chartArea.bottom);

    if (
      state.dragging &&
      state.startX !== null &&
      state.startY !== null &&
      state.currentX !== null &&
      state.currentY !== null
    ) {
      const payload = isCascadeMode
        ? buildCascadeSelectionPayload(
            chart,
            options,
            state.activeDatasetIndex,
            state.startX,
            state.startY,
            state.currentX,
            state.currentY,
          )
        : buildSelectionPayload(chart, options, state.startX, state.startY, state.currentX, state.currentY);

      if (payload) {
        clearSelectionState(chartWithSelectionZoom, options, false);
        state.suppressClickLock = 2;
        options.onZoom?.(payload);
        return;
      }
    }

    clearSelectionState(chartWithSelectionZoom, options, false);
  },

  afterDestroy(chart) {
    const chartWithSelectionZoom = chart as IChartWithSelectionZoom;

    restoreViewportTransform(chartWithSelectionZoom);
    clearSelectionState(chartWithSelectionZoom, undefined, false, false);
    removeNativeSelection(chartWithSelectionZoom);
    removeSelectionOverlay(chartWithSelectionZoom);
    delete chartWithSelectionZoom.selectionZoom;
  },

  afterDraw(chart, _args, pluginOptions?: ISelectionZoomPluginOptions) {
    const options = pluginOptions ?? {};
    const chartWithSelectionZoom = chart as IChartWithSelectionZoom;

    restoreViewportTransform(chartWithSelectionZoom);

    if (options.enabled === false) {
      return;
    }

    if (canUseNativeSelection(options)) {
      return;
    }

    const state = chartWithSelectionZoom.selectionZoom;

    if (
      !state ||
      !state.mouseDown ||
      state.startX === null ||
      state.startY === null ||
      state.currentX === null ||
      state.currentY === null
    ) {
      return;
    }

    drawSelectionOverlay(chart, state, options);
  },
};
